/**
 * Flow Configuration (FLOW_CONFIG)
 *
 * This is the SOURCE OF TRUTH for all modal workflows.
 * It defines the steps, navigation paths, and metadata for each flow.
 *
 * EXTENSIBILITY GUIDE:
 * ====================
 *
 * To add a new step:
 * 1. Create the step component in ./modals/
 * 2. Register it in component-registry.ts
 * 3. Add the step config below with unique ID
 * 4. Update `next`/`previous` pointers as needed
 *
 * To add a new flow:
 * 1. Add the flow type to FlowType union in flow-types.ts
 * 2. Define the flow config below
 * 3. Create all required step components
 */
import { LISTING_TYPE } from "@unreserved/constants/auction";

import type {
  ConditionalNext,
  FlowConfig,
  FlowStepConfig,
  NextConfig,
  NextResolverContext,
  NextResolverFn,
} from "@/app/_components/flow-modal/flow-types";

// ============================================================================
// Helper: Create step config with sensible defaults
// ============================================================================

function createStep(
  id: string,
  component: string,
  title: string,
  options: Partial<Omit<FlowStepConfig, "id" | "component" | "title">> = {},
): FlowStepConfig {
  return {
    id,
    component,
    title,
    next: null,
    ...options,
  };
}

// ============================================================================
// PRIVATE SALE VENDOR FLOW
// ============================================================================

/**
 * Private Sale Vendor flow for handling offers:
 * - offer_details: Shows offer with 3 action buttons (Counter, Reject, Accept)
 * - Each button leads to a different branch
 *
 * Branching is handled via explicit nextStepId passed from the component
 */
const PRIVATE_SALE_VENDOR_FLOW: Record<string, FlowStepConfig> = {
  offer_details: createStep("offer_details", "OfferDetailsModal", "Offer Details", {
    description: "Review the offer and take action",
    // Dynamic next - component will pass nextStepId explicitly based on button clicked
    next: null, // Terminal by default, component overrides with nextStepId
  }),

  payment_receipts: createStep("payment_receipts", "PaymentReceiptsModal", "Deposit Receipts", {
    description: "Download deposit receipts",
    previous: "offer_details",
    next: null,
  }),

  //   property_details: createStep("property_details", "PropertyDetailsModal", "Property Details", {
  // description: "Tell us about your property",
  // // EXAMPLE: Conditional branching based on property type
  // // Land properties skip straight to pricing, others go to detailed pricing
  // next: {
  //   default: "pricing",
  //   conditions: [
  //     // If it's a land sale, skip to a simpler pricing flow
  //     { when: (ctx) => ctx.data.propertyType === "land", then: "pricing" },
  //     // If API response indicates premium property, show premium options
  //     {
  //       when: (ctx) => (ctx.apiResponse as { isPremium?: boolean })?.isPremium === true,
  //       then: "premium_options",
  //     },
  //   ],
  // },
  // previous: "intro",

  // Counter branch
  counter_offer: createStep("counter_offer", "CounterOfferModal", "Counter Offer", {
    description: "Submit your counter offer",
    previous: "offer_details",
    next: "counter_offer_confirmation",
  }),

  counter_offer_confirmation: createStep(
    "counter_offer_confirmation",
    "CounterOfferConfirmationModal",
    "Counter Offer Confirmation",
    {
      description: "Confirm your counter offer",
      previous: "counter_offer",
      next: "counter_success",
    },
  ),

  counter_success: createStep("counter_success", "CounterSuccessModal", "Counter Sent", {
    description: "Your counter offer has been sent",
    next: null,
    meta: { isTerminal: true },
    refreshOnClose: true,
  }),

  // Reject branch
  reject_offer: createStep("reject_offer", "RejectOfferModal", "", {
    description: "Confirm rejection of the offer",
    previous: "offer_details",
    next: "null",
    meta: { isTerminal: true },
    refreshOnClose: true,
  }),

  // Accept branch
  accept_offer: createStep("accept_offer", "AcceptOfferModal", "", {
    description: "Confirm acceptance of the offer",
    previous: "offer_details",
    next: "accept_success",
  }),

  accept_success: createStep("accept_success", "AcceptSuccessModal", "Offer Accepted", {
    description: "Congratulations! The offer has been accepted",
    next: "wait_for_contract_of_sale",
    refreshOnClose: true,
  }),
  wait_for_contract_of_sale: createStep("wait_for_contract_of_sale", "WaitForContractOfSale", "", {
    description: "Waiting for contract of sale to be prepared",
    next: "contract_signed_success",
    refreshOnClose: true,
  }),
  contract_signed_success: createStep(
    "contract_signed_success",
    "ContractSignedSuccessModal",
    "Contract Signed Successfully",
    {
      description: "Your contract has been signed successfully",
      next: null,
      meta: { isTerminal: true },
      refreshOnClose: true,
    },
  ),
};

// ============================================================================
// PRIVATE SALE BUYER FLOW
// ============================================================================

/**
 * Private Sale Buyer flow for making offers:
 * - make_offer: Buyer submits an offer
 * - wait_for_contract: Buyer waits for contract of sale
 */
const PRIVATE_SALE_BUYER_FLOW: Record<string, FlowStepConfig> = {
  make_offer: createStep("make_offer", "MakeOfferModal", "Make An Offer", {
    description: "Submit your offer on this property",
    next: "offer_under_review",
  }),

  offer_under_review: createStep("offer_under_review", "OfferUnderReviewModal", "Offer Submitted", {
    description: "Offer is under admin review pending approval",
    next: null,
    meta: { isTerminal: true },
  }),

  wait_for_contract_of_sale: createStep("wait_for_contract_of_sale", "WaitForContractModal", "", {
    description: "Waiting for contract of sale to be prepared",
    next: "legal_agreement",
  }),

  legal_agreement: createStep("legal_agreement", "LegalAgreementModal", "Legal Agreement", {
    description: "Review the legal agreement",
    previous: "wait_for_contract_of_sale",
    next: "submit_offer_confirmation",
  }),

  submit_offer_confirmation: createStep(
    "submit_offer_confirmation",
    "SubmitOfferConfirmationModal",
    "",
    {
      description: "Confirm submission of the offer",
      previous: "legal_agreement",
      next: "submit_offer_success",
    },
  ),
  submit_offer_success: createStep(
    "submit_offer_success",
    "SubmitOfferSuccessModal",
    "Offer Submitted",
    {
      description: "Offer submitted successfully",
      previous: "submit_offer_confirmation",
      next: null,
    },
  ),

  // Offer Accepted by vendor
  payment_details: createStep("payment_details", "PaymentDetailsModal", "Payment Details", {
    description: "Upload your payment details",
    previous: "submit_offer_success",
    next: "upload_receipt",
  }),
  upload_receipt: createStep("upload_receipt", "UploadReceiptModal", "Upload Receipt", {
    description: "Upload your receipt",
    previous: "payment_details",
    next: "payment_succesful",
  }),
  payment_succesful: createStep(
    "payment_succesful",
    "PaymentSuccesfulModal",
    "Payment Successful",
    {
      description: "Payment successful",
      previous: "upload_receipt",
      next: null,
    },
  ),

  // Counter offer by vendor

  counter_offer_received: createStep(
    "counter_offer_received",
    "CounterOfferReceivedModal",
    "Counter Offer",
    {
      description: "Counter offer received",
      previous: "reject_offer",
      next: "wait_for_contract_of_sale",
    },
  ),
  accept_offer_confirmation: createStep(
    "accept_offer_confirmation",
    "AcceptOfferConfirmationModal",
    "Accept Offer",
    {
      description: "Accept offer confirmation",
      previous: "counter_offer_received",
      next: "accept_counter_success",
    },
  ),
  accept_counter_success: createStep(
    "accept_counter_success-modal",
    "AcceptCounterSuccessModal",
    "Counter Offer Accepted",
    {
      description: "Counter offer accepted successfully",
      previous: "accept_offer_confirmation",
      next: null,
    },
  ),
  reject_offer: createStep("reject_offer", "RejectOfferBuyerModal", "Reject Offer", {
    description: "Reject offer confirmation",
    previous: "counter_offer_received",
    next: null,
  }),

  // Offer rejected by vendor
  offer_rejected: createStep("offer_rejected", "OfferRejectedModal", "Offer Rejected", {
    description: "Rejected Offer",
    previous: "reject_offer",
    next: null,
  }),

  // Offer summary
  offer_summary: createStep("offer_summary", "OfferSummaryModal", "Offer Summary", {
    description: "Review the offer summary",
    previous: null,
    next: null,
  }),
};

// ============================================================================
// LIVE AUCTION BUYER FLOW
// ============================================================================

/**
 * Live Auction Buyer flow for participating in live auctions:
 * - start_verification: Start verification process
 * - make_offer: Make an offer on auction property
 * - place_bid: Place a bid during live auction
 * - auction_won/lost/passedin: Auction outcome modals
 */
const LIVE_AUCTION_BUYER_FLOW: Record<string, FlowStepConfig> = {
  start_verification: createStep(
    "start_verification",
    "StartVerificationModal",
    "Stripe Verification",
    {
      description: "Begin the verification process",
      next: null,
      listingType: LISTING_TYPE.LIVE_AUCTION,
    },
  ),

  make_offer: createStep("make_offer", "LiveAuctionMakeOfferModal", "Complete Offer Submission", {
    description: "Submit your offer on this auction property",
    next: "submit_offer_confirmation",
  }),

  submit_offer_confirmation: createStep(
    "submit_offer_confirmation",
    "LiveAuctionSubmitOfferConfirmationModal",
    "Confirm Offer Submission",
    {
      description: "Confirm submission of the offer",
      previous: "make_offer",
      next: "offer_submitted_success",
    },
  ),

  offer_under_review: createStep("offer_under_review", "OfferUnderReviewModal", "Offer Submitted", {
    description: "Offer is under admin review pending approval",
    previous: "submit_offer_confirmation",
    next: null,
  }),

  wait_for_contract_of_sale: createStep(
    "wait_for_contract_of_sale",
    "WaitForContractSigningModal",
    "",
    {
      description: "Waiting for contract of sale to be prepared",
      previous: "offer_submitted_success",
      next: "contract_signed_success",
    },
  ),

  contract_signed_success: createStep(
    "contract_signed_success",
    "LiveAuctionBuyerContractSignSuccessModal",
    "",
    {
      description: "Contract signed successfully",
      previous: "wait_for_contract_of_sale",
      next: "payment_details",
    },
  ),

  payment_details: createStep(
    "payment_details",
    "LiveAuctionPaymentDetailsModal",
    "Payment Details",
    {
      description: "View payment details",
      previous: "contract_signed_success",
      next: "upload_receipt",
    },
  ),

  auction_cancelled: createStep("auction_cancelled", "AuctionCancelledModal", "", {
    description: "This auction has been cancelled",
    next: null,
    dismissible: true,
  }),

  place_bid: createStep("place_bid", "PlaceBidModal", "Place Bid", {
    description: "Place your bid on this property",
    next: null,
  }),

  view_bid_activity: createStep("view_bid_activity", "ViewBidActivityModal", "Bid Activity", {
    description: "View all bid activity for this auction",
    next: null,
  }),

  auction_won: createStep("auction_won", "AuctionWonModal", "", {
    description: "Congratulations! You won the auction",
    next: "make_offer",
    dismissible: true,
  }),

  auction_lost: createStep("auction_lost", "AuctionLostModal", "", {
    description: "Unfortunately, you did not win this auction",
    next: null,
    dismissible: true,
  }),

  auction_passedin: createStep("auction_passedin", "AuctionPassedinModal", "", {
    description: "This auction was passed in",
    next: null,
    dismissible: true,
  }),

  upload_receipt: createStep("upload_receipt", "LiveAuctionUploadReceiptModal", "Upload Receipt", {
    description: "Upload your payment receipt",
    previous: "payment_details",
    next: "payment_successful",
  }),

  payment_successful: createStep("payment_successful", "PaymentSuccessfulModal", "", {
    description: "Payment completed successfully",
    previous: "upload_receipt",
    next: null,
    dismissible: true,
  }),
};

// ============================================================================
// LIVE AUCTION VENDOR FLOW
// ============================================================================

/**
 * Live Auction Vendor flow for managing live auctions:
 * - bid_activity: View all bids on the property
 * - pause_auction: Pause the ongoing auction
 * - modify_reserve_price: Modify the reserve price
 * - property_sold/not_sold: Auction outcome modals
 */
const LIVE_AUCTION_VENDOR_FLOW: Record<string, FlowStepConfig> = {
  pause_auction: createStep("pause_auction", "PauseAuctionModal", "", {
    description: "Pause the ongoing auction",
    next: "auction_paused",
  }),

  resume_auction: createStep("resume_auction", "ResumeAuctionModal", "", {
    description: "Resume the ongoing reverse auction",
    next: null,
  }),

  submit_vendor_bid: createStep("submit_vendor_bid", "SubmitVendorBidModal", "", {
    description: "Place a vendor bid on the auction",
    next: null,
  }),

  modify_reserve_price: createStep("modify_reserve_price", "ModifyReservePriceModal", "", {
    description: "Change the reserve price for this auction",
    next: null,
  }),

  view_bid_activity: createStep("view_bid_activity", "BidActivityModal", "Bid Activity", {
    description: "View bid activity for this auction",
    next: null,
  }),

  property_sold: createStep("property_sold", "PropertySoldModal", "", {
    description: "Congratulations! Your property has been sold",
    next: "wait_for_contract_signing",
  }),

  property_not_sold: createStep("property_not_sold", "PropertyNotSoldModal", "", {
    description: "Your property was not sold at auction",
    next: null,
    dismissible: true,
    // refreshOnClose: true,  // TODO: Team, Uncomment this when the API is integrated ( this isneeded for vendor's view only, as same page serves 3 tabs including auction offers)
  }),

  auction_cannot_proceed: createStep("auction_cannot_proceed", "CannotProceedModal", "", {
    description: "Auction cancelled due to no registered bidders",
    next: null,
    dismissible: true,
    // refreshOnClose: true,  same here as above.
  }),

  wait_for_contract_signing: createStep(
    "wait_for_contract_signing",
    "LiveAuctionVendorWaitForContractSigningModal",
    "",
    {
      description: "Waiting for contract to be signed",
      next: "contract_sign_success",
    },
  ),

  contract_sign_success: createStep(
    "contract_sign_success",
    "LiveAuctionVendorContractSignSuccessModal",
    "",
    {
      description: "Contract signed successfully",
      next: null,
    },
  ),

  offer_submitted_success: createStep(
    "offer_submitted_success",
    "LiveAuctionVendorOfferSubmittedSuccessModal",
    "Offer Submitted",
    {
      description: "Offer submitted successfully",
      next: null,
    },
  ),

  cannot_proceed: createStep("cannot_proceed", "CannotProceedModal", "Cannot Proceed", {
    description: "Unable to proceed with this action",
    next: null,
  }),
};

// ============================================================================
// REVERSE AUCTION BUYER FLOW
// ============================================================================

/**
 * Reverse Auction Buyer flow for participating in reverse auctions:
 * - start_verification: Start verification process
 * - make_offer: Make an offer on reverse auction property
 * - set_price_drop_alert: Set alerts for price drops
 * - auction_won/lost: Auction outcome modals
 */
const REVERSE_AUCTION_BUYER_FLOW: Record<string, FlowStepConfig> = {
  start_verification: createStep(
    "start_verification",
    "StartVerificationModal",
    "Stripe Verification",
    {
      description: "Begin the verification process",
      next: null,
      listingType: LISTING_TYPE.REVERSE_AUCTION,
    },
  ),

  set_price_drop_alert: createStep("set_price_drop_alert", "SetPriceDropAlertModal", "", {
    description: "Set alerts for when the price drops",
    next: null,
  }),
  buy_now: createStep("buy_now", "BuyNowModal", "", {
    description: "Buy now at the current price",
    next: null,
  }),
};

// ============================================================================
// REVERSE AUCTION VENDOR FLOW
// ============================================================================

/**
 * Reverse Auction Vendor flow for managing reverse auctions:
 * - bid_activity: View all bids on the property
 * - pause_auction: Pause the ongoing reverse auction
 * - modify_reserve_price: Modify the reserve price
 * - property_sold/not_sold: Auction outcome modals
 */
const REVERSE_AUCTION_VENDOR_FLOW: Record<string, FlowStepConfig> = {
  pause_auction: createStep("pause_auction", "PauseAuctionModal", "", {
    description: "Pause the ongoing reverse auction",
    next: "auction_paused",
  }),
  resume_auction: createStep("resume_auction", "ResumeAuctionModal", "", {
    description: "Resume the ongoing reverse auction",
    next: null,
  }),

  modify_reserve_price: createStep("modify_reserve_price", "ModifyReservePriceModal", "", {
    description: "Change the reserve price for this reverse auction",
    next: null,
  }),

  property_sold: createStep("property_sold", "PropertySoldModal", "", {
    description: "Congratulations! Your property has been sold",
    next: "wait_for_contract_signing",
  }),

  property_not_sold: createStep("property_not_sold", "PropertyNotSoldModal", "", {
    description: "Your property was not sold at auction",
    next: null,
    dismissible: true,
  }),

  wait_for_contract_signing: createStep(
    "wait_for_contract_signing",
    "LiveAuctionVendorWaitForContractSigningModal",
    "",
    {
      description: "Waiting for contract to be signed",
      next: "contract_sign_success",
    },
  ),

  contract_sign_success: createStep(
    "contract_sign_success",
    "LiveAuctionVendorContractSignSuccessModal",
    "",
    {
      description: "Contract signed successfully",
      next: null,
    },
  ),

  offer_submitted_success: createStep(
    "offer_submitted_success",
    "LiveAuctionVendorOfferSubmittedSuccessModal",
    "Offer Submitted",
    {
      description: "Offer submitted successfully",
      next: null,
    },
  ),

  auction_cannot_proceed: createStep("auction_cannot_proceed", "CannotProceedModal", "", {
    description: "Auction cancelled due to no registered bidders",
    next: null,
    dismissible: true,
  }),
};

// ============================================================================
// COMBINED FLOW CONFIGURATION
// ============================================================================

export const FLOW_CONFIG: FlowConfig = {
  PRIVATE_SALE_VENDOR: PRIVATE_SALE_VENDOR_FLOW,
  PRIVATE_SALE_BUYER: PRIVATE_SALE_BUYER_FLOW,
  LIVE_AUCTION_BUYER: LIVE_AUCTION_BUYER_FLOW,
  LIVE_AUCTION_VENDOR: LIVE_AUCTION_VENDOR_FLOW,
  REVERSE_AUCTION_BUYER: REVERSE_AUCTION_BUYER_FLOW,
  REVERSE_AUCTION_VENDOR: REVERSE_AUCTION_VENDOR_FLOW,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the entry point (first step) for a flow.
 * Convention: First step is always "intro" unless specified otherwise.
 */
export function getFlowEntryPoint(flowType: keyof typeof FLOW_CONFIG): string {
  const flow = FLOW_CONFIG[flowType];
  // Find entry point: step with no previous, or fallback to first step
  const entryStep = Object.values(flow).find((step) => !step.previous);
  return entryStep?.id ?? Object.keys(flow)[0] ?? "offer_details";
}

/**
 * Get step configuration by flow type and step ID.
 */
export function getStepConfig(
  flowType: keyof typeof FLOW_CONFIG,
  stepId: string,
): FlowStepConfig | null {
  return FLOW_CONFIG[flowType]?.[stepId] ?? null;
}

/**
 * Extract a static next step ID from a NextConfig.
 * Used for building ordered step lists - only follows static/default paths.
 */
function getStaticNextId(nextConfig: NextConfig): string | null {
  if (typeof nextConfig === "string" || nextConfig === null) {
    return nextConfig;
  }
  if (isConditionalNext(nextConfig)) {
    return nextConfig.default;
  }
  // Functions are dynamic - can't determine statically
  return null;
}

/**
 * Get all steps for a flow as an ordered array (following next pointers).
 * NOTE: For conditional/dynamic nexts, follows the default path.
 */
export function getOrderedSteps(flowType: keyof typeof FLOW_CONFIG): FlowStepConfig[] {
  const flow = FLOW_CONFIG[flowType];
  if (!flow) return [];

  const entryPoint = getFlowEntryPoint(flowType);
  const steps: FlowStepConfig[] = [];
  const visited = new Set<string>();

  let currentId: string | null = entryPoint;
  while (currentId && !visited.has(currentId)) {
    const currentStep: FlowStepConfig | undefined = flow[currentId];
    if (currentStep) {
      visited.add(currentId);
      steps.push(currentStep);
      // Extract static next ID (follows default for conditional)
      currentId = getStaticNextId(currentStep.next);
    } else {
      break;
    }
  }

  return steps;
}

/**
 * Calculate progress percentage for a given step in a flow.
 */
export function getFlowProgress(flowType: keyof typeof FLOW_CONFIG, currentStepId: string): number {
  const steps = getOrderedSteps(flowType);
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  if (currentIndex === -1 || steps.length === 0) return 0;
  return Math.round(((currentIndex + 1) / steps.length) * 100);
}

// ============================================================================
// Next Step Resolution
// ============================================================================

/**
 * Type guard: Check if NextConfig is a ConditionalNext object.
 */
function isConditionalNext(next: NextConfig): next is ConditionalNext {
  return typeof next === "object" && next !== null && "default" in next && "conditions" in next;
}

/**
 * Type guard: Check if NextConfig is a NextResolverFn function.
 */
function isNextResolverFn(next: NextConfig): next is NextResolverFn {
  return typeof next === "function";
}

/**
 * Resolve the next step ID from a NextConfig.
 *
 * This is the core resolution logic that handles all navigation patterns:
 * 1. Static string - return as-is
 * 2. null - terminal step
 * 3. ConditionalNext - evaluate conditions in order
 * 4. NextResolverFn - call function with context
 *
 * @param nextConfig - The next configuration from the step
 * @param context - Context for resolution (data, apiResponse, etc.)
 * @returns The resolved step ID or null
 */
export function resolveNextStep(
  nextConfig: NextConfig,
  context: NextResolverContext,
): string | null {
  // Static string or null - return directly
  if (typeof nextConfig === "string" || nextConfig === null) {
    return nextConfig;
  }

  // Function resolver - call with context
  if (isNextResolverFn(nextConfig)) {
    return nextConfig(context);
  }

  // Conditional next - evaluate conditions
  if (isConditionalNext(nextConfig)) {
    for (const condition of nextConfig.conditions) {
      if (condition.when(context)) {
        return condition.then;
      }
    }
    return nextConfig.default;
  }

  // Fallback - should never reach here if types are correct
  console.error("[resolveNextStep] Unknown next config type:", nextConfig);
  return null;
}

// ============================================================================
// Helper Builders for Conditional Next
// ============================================================================

/**
 * Create a conditional next configuration.
 * Makes it easy to define branching logic declaratively.
 *
 * @example
 * ```ts
 * next: conditionalNext("default_step", [
 *   { when: (ctx) => ctx.data.propertyType === "land", then: "land_step" },
 *   { when: (ctx) => ctx.apiResponse?.needsVerification, then: "verification" },
 * ])
 * ```
 */
export function conditionalNext(
  defaultStep: string | null,
  conditions: ConditionalNext["conditions"],
): ConditionalNext {
  return { default: defaultStep, conditions };
}

/**
 * Create a dynamic next resolver function.
 *
 * @example
 * ```ts
 * next: dynamicNext((ctx) => {
 *   if (ctx.apiResponse?.redirectTo) return ctx.apiResponse.redirectTo;
 *   if (ctx.data.skipVerification) return "success";
 *   return "verification";
 * })
 * ```
 */
export function dynamicNext(resolver: NextResolverFn): NextResolverFn {
  return resolver;
}
